import React, { PropsWithChildren, useMemo, useState } from "react";
import { usePaginationHelper } from "../../../../lib/hooks/usePaginationHelper";
import { useInsightsContext } from "../../Context/InsightsContext";

import { GeneralStatsByDesksResponse } from "../../../../api/grpc/insights/insights";
import { PaginationContext } from "../../../../lib/context/Pagination/PaginationContext";
import { trimText } from "../../../../lib/helpers/trimText";
import { Pagination } from "../../../shared/Pagination/Pagination";
import {
  StructureTable,
  StructureTableColumn,
  StructureTableRows,
} from "../../../shared/StructureTable/StructureTable";
import {
  sortStructureTableData,
  SortDirection,
} from "../../../shared/StructureTable/sortUtils";

const columns: StructureTableColumn[] = [
  {
    key: "desk_name",
    header: "Desk name",
    sortable: true,
    sortKey: "name",
  },
  {
    key: "desk_reservations",
    header: "Reservations",
    sortable: true,
    sortKey: "totalMeetings",
  },
  {
    key: "avg_daily_reservations",
    header: "Avg. daily reservations",
    sortable: true,
    sortKey: "avgMeetings",
  },
  {
    key: "avg_reservations_duration",
    header: "Avg. reservation duration (min)",
    sortable: true,
    sortKey: "avgMeetingTime",
  },
  {
    key: "occupancy",
    header: "Occupancy",
    sortable: true,
    sortKey: "occupancy",
  },
  {
    key: "no_shows",
    header: "No-shows",
    sortable: true,
    sortKey: "noShows",
  },
];

export const InsightsExpandedDesks = ({
  data,
  showPagination,
}: PropsWithChildren<{
  data: GeneralStatsByDesksResponse;
  showPagination: boolean;
}>) => {
  const { page, pageSize, pagination } = usePaginationHelper(data.data.length);
  const { setInsightsLocation } = useInsightsContext();
  const [sortedColumnKey, setSortedColumnKey] = useState<string>("desk_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const indexOfLastDesk = page * pageSize;
  const indexOfFirstDesk = indexOfLastDesk - pageSize;
  const sortedDeskData = useMemo(
    () =>
      sortStructureTableData(
        data.data,
        columns,
        sortedColumnKey,
        sortDirection
      ),
    [data.data, sortedColumnKey, sortDirection]
  );
  const currentDeskData = useMemo(
    () => sortedDeskData.slice(indexOfFirstDesk, indexOfLastDesk),
    [indexOfFirstDesk, indexOfLastDesk, sortedDeskData]
  );

  const rows: StructureTableRows[] = currentDeskData.map((item) => {
    return {
      desk_name: (
        <h6
          className="mb-0"
          title={item.name}
          onClick={() => {
            setInsightsLocation({
              locationId: item.id,
              locationName: item.name,
              locationType: "Desk",
            });
          }}
        >
          {trimText(item.name, 27)}
        </h6>
      ),
      desk_reservations: <h6>{item.totalMeetings}</h6>,
      avg_daily_reservations: (
        <h6>{item.avgMeetingTime > 0 ? Math.round(item.avgMeetings) : 0}</h6>
      ),
      avg_reservations_duration: (
        <h6>{item.avgMeetingTime > 0 ? Math.round(item.avgMeetingTime) : 0}</h6>
      ),
      occupancy: (
        <h6>{item.occupancy > 0 ? Math.round(item.occupancy) + "%" : 0}</h6>
      ),
      no_shows: <h6>{item.noShows > 0 ? Math.round(item.noShows) : 0}</h6>,
    };
  });

  const handleSort = (columnKey: string) => {
    if (pagination.page !== 1) {
      pagination.setPage(1);
    }

    if (columnKey === sortedColumnKey) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    setSortedColumnKey(columnKey);
    setSortDirection("desc");
  };

  return (
    <PaginationContext.Provider value={pagination}>
      <StructureTable
        rows={rows}
        columns={columns}
        className="StructureTable--rooms InsightsExpandedRooms mt-5"
        onSort={handleSort}
        sortedColumnKey={sortedColumnKey}
        sortDirection={sortDirection}
      />
      {showPagination && <Pagination />}
    </PaginationContext.Provider>
  );
};
